import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import type { CashflowData, WaterfallItem, CashPositionItem } from './use-finance-cashflow.js';

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtWeek(w: string): string {
  const d = new Date(w + 'T12:00:00');
  return isNaN(d.getTime()) ? w : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const TYPE_COLOR: Record<string, string> = { revenue: C.green, expense: C.red, net: C.gold, income: C.green };

function WaterfallRow({ item }: { item: WaterfallItem }) {
  const color = TYPE_COLOR[item.type] || C.text;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, padding: '10px 0', borderBottom: `1px solid ${C.borderSoft}`, alignItems: 'center' }}>
      <span style={{ fontSize: 13, color: item.type === 'net' ? C.gold : C.text, fontWeight: item.type === 'net' ? 700 : 400 }}>{item.label}</span>
      <span style={{ fontSize: 14, color, fontWeight: 600, textAlign: 'right' }}>{fmt(item.amount)}</span>
      <span style={{ fontSize: 12, color: C.textMuted, textAlign: 'right', minWidth: 80 }}>{fmt(item.running_total)}</span>
    </div>
  );
}

function LedgerRow({ item }: { item: CashPositionItem }) {
  const color = TYPE_COLOR[item.type] || C.text;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.borderSoft}` }}>
      <span style={{ fontSize: 13, color: item.type === 'net' ? C.gold : C.text, fontWeight: item.type === 'net' ? 700 : 400 }}>{item.label}</span>
      <span style={{ fontSize: 14, color, fontWeight: 600 }}>{fmt(item.amount)}</span>
    </div>
  );
}

interface Props { data: CashflowData; onRefresh: () => void; }

export default function FinanceCashflow({ data, onRefresh }: Props) {
  const { waterfall, cumulative, cash_position, period } = data;
  const netItem = waterfall.find(w => w.type === 'net');

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: netItem && netItem.amount >= 0 ? C.green : C.red }}>{netItem ? fmt(netItem.amount) : '\u2014'}</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>Net cash \u00b7 {period.weeks_included} week{period.weeks_included !== 1 ? 's' : ''}</div>
        </div>
        <button onClick={onRefresh} style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Refresh</button>
      </div>

      {/* Waterfall */}
      {waterfall.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 8 }}>Cashflow waterfall</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 0, padding: '0 0 4px', borderBottom: `1px solid ${C.borderSoft}`, marginBottom: 4 }}>
            <span style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase' }}>Item</span>
            <span style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', textAlign: 'right' }}>Amount</span>
            <span style={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', textAlign: 'right', minWidth: 80 }}>Running</span>
          </div>
          {waterfall.map((item, i) => <WaterfallRow key={i} item={item} />)}
        </div>
      )}

      {/* Cumulative (multi-week) */}
      {cumulative.length > 1 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 8 }}>Cumulative weekly</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>{['Week', 'Revenue', 'Payroll', 'Expenses', 'Net'].map(h => (
                <th key={h} style={{ padding: '6px 8px', textAlign: h === 'Week' ? 'left' : 'right', color: C.textMuted, fontWeight: 500, fontSize: 10, textTransform: 'uppercase', borderBottom: `1px solid ${C.borderSoft}` }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {cumulative.map(w => (
                <tr key={w.week_ending} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                  <td style={{ padding: '6px 8px', color: C.text }}>{fmtWeek(w.week_ending)}</td>
                  <td style={{ padding: '6px 8px', color: C.green, textAlign: 'right' }}>{fmt(w.revenue)}</td>
                  <td style={{ padding: '6px 8px', color: C.text, textAlign: 'right' }}>{fmt(w.payroll)}</td>
                  <td style={{ padding: '6px 8px', color: C.red, textAlign: 'right' }}>{fmt(w.expenses)}</td>
                  <td style={{ padding: '6px 8px', color: w.net >= 0 ? C.green : C.red, textAlign: 'right', fontWeight: 600 }}>{fmt(w.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cash position ledger */}
      {cash_position.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 8 }}>Cash position</div>
          {cash_position.map((item, i) => <LedgerRow key={i} item={item} />)}
        </div>
      )}
    </div>
  );
}
